import React, { useRef } from 'react';
import * as S from './Checkbox.styles';
import { RequiredStar } from '@/components/UI/RequiredStar/RequiredStar';
import { CheckboxProps } from '@/components/UI/Checkbox/Checkbox.types';
import { getDataStatAttr } from '@/utils/helpers';
import { CheckIconGradient } from '@/components/UI/Icons/Icons';

export const Checkbox: React.FC<CheckboxProps> = ({
  value,
  label,
  labelIcon,
  helperText,
  hasError,
  appearance = 'switch',
  size = 'lg',
  onChangeHook,
  labelPosition = 'left',
  color = 'primary',
  action,
  customContent,
  dataStat,
  hasSwitch = true,
  hasBorderBottom = true,
  dataTestId,
  hasSmallGap,
  highlightLabel,
  ...props
}) => {
  const checkboxRef = useRef<HTMLInputElement>(null);

  function handleCheckboxWrapperClick() {
    if (checkboxRef.current) {
      checkboxRef.current.click();
    }
  }

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    props.onChange?.(e);
    onChangeHook?.();
  }

  const shouldHighlightLabel =
    highlightLabel || appearance === 'radioCheckIcon';

  return (
    <S.Wrapper
      {...getDataStatAttr({ dataStat })}
      data-testid={dataTestId}
      $appearance={appearance}
      $size={size}
      $color={color}
      $labelPosition={labelPosition}
      $hasBorderBottom={hasBorderBottom}
      onClick={handleCheckboxWrapperClick}
      className={props.className}
      $hasSmallGap={hasSmallGap}
      data-selected={value}
    >
      {action}

      {customContent}

      {label && !labelIcon && (
        <S.Label
          as="label"
          htmlFor={props.name}
          $isChecked={shouldHighlightLabel && value}
        >
          {label} {props.required && <RequiredStar />}
        </S.Label>
      )}

      {label && labelIcon && (
        <S.LabelBox htmlFor={props.name}>
          <S.Label as="span" $isChecked={shouldHighlightLabel && value}>
            {label} {props.required && <RequiredStar />}
          </S.Label>

          {labelIcon}
        </S.LabelBox>
      )}

      <S.Switch $appearance={appearance} $isVisible={hasSwitch}>
        <S.Box
          {...props}
          onChange={onChange}
          ref={checkboxRef}
          checked={value}
          type="checkbox"
          data-testid="Checkbox"
        />

        {appearance === 'radioCheckIcon' ? (
          value && <CheckIconGradient />
        ) : (
          <S.Knob $appearance={appearance} />
        )}
      </S.Switch>

      {helperText && (
        <S.HelperText $variant="4" $hasError={hasError}>
          {helperText}
        </S.HelperText>
      )}
    </S.Wrapper>
  );
};
